import { useState, useEffect, SyntheticEvent } from 'react';
import {
  Alert, Box, Button, Card, CardActions, CardContent,
  CircularProgress, Snackbar, TextField, Typography, Backdrop
} from '@mui/material';
import * as yup from 'yup';
import { useAuthContext } from '../../contexts/AuthContext';
const loginSchema = yup.object().shape({
  usuario: yup.string().required('Usuário é obrigatório'),
  senha: yup.string().required('Senha é obrigatória').min(5, 'Senha deve ter no mínimo 5 caracteres'),
});
interface ILoginProps {
  children: React.ReactNode;
}
export const Login: React.FC<ILoginProps> = ({ children }) => {
  const { isAuthenticated, login, isLoading: isAuthLoading } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [credentials, setCredentials] = useState({
    usuario: '',
    senha: ''
  });
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'warning' as 'warning' | 'error' | 'info' | 'success'
  });
  useEffect(() => {
    setErrors({});
  }, [credentials]);

  const handleCloseNotification = (
    event?: SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification(prev => ({ ...prev, open: false }));
  };
  const handleAlertClose = (event?: SyntheticEvent) => {
    setNotification(prev => ({ ...prev, open: false }));
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setIsLoading(true);

    try {
      await loginSchema.validate(credentials, { abortEarly: false });

      const result = await login(credentials.usuario, credentials.senha);
      if (typeof result === 'string') {
        setNotification({
          open: true,
          message: result,
          severity: 'error'
        });
      }
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const validationErrors: Record<string, string> = {};
        err.inner.forEach(error => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
      } else if (err instanceof Error) {
        setNotification({
          open: true,
          message: err.message || 'Erro ao fazer login',
          severity: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  if (isAuthenticated) return (
    <>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isAuthLoading}
      >
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">Atualizando dados do sistema...</Typography>
        </Box>
      </Backdrop>
      {!isAuthLoading && children}
    </>
  );
  return (
    <Box
      width='100vw'
      height='100vh'
      display='flex'
      alignItems='center'
      justifyContent='center'
      component="form"
      onSubmit={handleSubmit}
    >
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleAlertClose}  // Usamos a função específica aqui
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Card>
        <CardContent>
          <Box display='flex' flexDirection='column' gap={2} width={350}>
            <Typography variant='h6' align='center'>Identifique-se</Typography>

            <TextField
              fullWidth
              name="usuario"
              label='Usuário'
              value={credentials.usuario}
              disabled={isLoading}
              error={!!errors.usuario}
              helperText={errors.usuario}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
            />

            <TextField
              fullWidth
              name="senha"
              label='Senha'
              type='password'
              value={credentials.senha}
              disabled={isLoading}
              error={!!errors.senha}
              helperText={errors.senha}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
            />
          </Box>
        </CardContent>
        <CardActions>
          <Box width='100%' display='flex' justifyContent='center'>
            <Button
              type="submit"
              variant='contained'
              disabled={isLoading}
              onClick={handleSubmit}
              endIcon={isLoading ? <CircularProgress size={20} /> : undefined}
              fullWidth
              sx={{ margin: 1 }}
            >
              Entrar
            </Button>
          </Box>
        </CardActions>
      </Card>
    </Box>
  );
};